/* HidroVerse SalesFlow — Service Worker registration helper.
 *
 * Registers /service-worker.js when:
 *   • running in production (avoids stale-cache headaches in dev/HMR), and
 *   • the browser supports it.
 *
 * Disable on localhost dev runs by setting REACT_APP_DISABLE_SW=true.
 */

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  if (process.env.REACT_APP_DISABLE_SW === "true") return;

  // Register immediately (do NOT wait for `load`). The `load` event can be
  // delayed several seconds by slow third-party scripts (PostHog,
  // Cloudflare challenge iframe, Google Fonts) — and Chrome Android
  // evaluates PWA installability on its own timer: if the SW isn't
  // registered in time, Chrome decides the site is NOT installable and
  // hides both the mini-infobar and the "Install app" menu option. That
  // verdict sticks until the next full reload.
  //
  // It's safe to register during parse; the browser defers the actual
  // install task off the main thread, so first paint is unaffected.
  navigator.serviceWorker
    .register("/service-worker.js", { scope: "/" })
    .then((reg) => {
      // Auto-reload when a new SW takes over — prevents users from seeing
      // a stale cached UI after a deploy. Without this, users would have
      // to manually hard-refresh (or worse, uninstall the PWA) to see
      // layout fixes like the onboarding step-rail change.
      let refreshing = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (refreshing) return;
        refreshing = true;
        window.location.reload();
      });

      // Poll for a new SW every 60s while the tab is open — catches
      // deploys mid-session.
      setInterval(() => {
        reg.update().catch(() => {});
      }, 60_000);
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("[hv] SW registration failed:", err);
    });

  // Wave 26.10.13 — recover from a stale-chunk failure. When the browser
  // tries to load a JS chunk that has been removed by a deploy, webpack
  // throws ChunkLoadError or the script returns HTML and we get
  // "Unexpected token '<'". One reload usually picks up the fresh
  // index.html with the new chunk hashes. We use a sessionStorage flag
  // so we don't loop reloading on a real bug.
  const RELOAD_FLAG = "hv_chunk_reload_v1";
  const recover = () => {
    try {
      if (sessionStorage.getItem(RELOAD_FLAG)) return; // already reloaded once this session
      sessionStorage.setItem(RELOAD_FLAG, "1");
      // eslint-disable-next-line no-console
      console.warn("[hv] stale chunk detected — reloading once to recover");
      // Force the SW cache to drop — unregister it so fresh code wins.
      navigator.serviceWorker?.getRegistrations?.().then((rs) => {
        Promise.all(rs.map((r) => r.unregister().catch(() => {}))).finally(() => {
          window.location.reload();
        });
      }).catch(() => window.location.reload());
    } catch {
      window.location.reload();
    }
  };

  window.addEventListener("error", (event) => {
    const msg = String(event?.message || "");
    const name = String(event?.error?.name || "");
    if (
      name === "ChunkLoadError"
      || msg.includes("Loading chunk")
      || msg.includes("Failed to fetch dynamically imported module")
      || (msg.includes("Unexpected token") && msg.includes("<"))
    ) {
      recover();
    }
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    const name = String(reason?.name || "");
    const msg = String(reason?.message || reason || "");
    if (
      name === "ChunkLoadError"
      || msg.includes("Loading chunk")
      || msg.includes("Failed to fetch dynamically imported module")
    ) {
      recover();
    }
  });
}
